import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Mail, Loader2, Send } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { track, makeVideoTracker } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WELCOME_VIDEO = `${API}/welcome-video`;

const ThankYou = () => {
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const vt = React.useRef(makeVideoTracker({ title: "Welcome from Sandra", id: "welcome" })).current;

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "You're Booked | Sassy Full Stack Fitness";
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.message.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact-sandra`, form);
      setSent(true);
      track("generate_lead", { form_type: "contact_sandra" }, "Contact");
    } catch (err) {
      setSent(true); // graceful — message is fire-and-forget on the server side
      track("generate_lead", { form_type: "contact_sandra" }, "Contact");
    } finally {
      setSending(false);
    }
  };

  const closeModal = () => {
    setOpen(false);
    setTimeout(() => {
      setSent(false);
      setForm({ name: "", email: "", message: "" });
    }, 250);
  };

  return (
    <div className="bg-background min-h-screen flex flex-col" data-testid="thank-you-page">
      <Header />
      <main className="flex-1 pt-28 md:pt-32 pb-24 px-[5%]">
        <div className="max-w-[880px] mx-auto text-center">
          {/* Booked badge */}
          <span
            className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/[0.1] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.28em] text-primary"
            data-testid="thankyou-badge"
          >
            <CheckCircle2 size={14} strokeWidth={2.5} /> Your Call Is Booked
          </span>

          <h1
            className="mt-6 font-display font-black uppercase leading-[1.02] text-[38px] md:text-[64px] text-white"
            style={{ letterSpacing: "0.01em" }}
            data-testid="thankyou-title"
          >
            Thank You — <span className="text-gold-gradient">You're All Set</span>
          </h1>

          <p className="mt-5 text-base md:text-lg text-white/70 font-medium max-w-2xl mx-auto leading-relaxed">
            Everything you need for your call is in your confirmation email, including how to add it
            to your calendar. Before you go, watch this quick welcome message from Sandra.
          </p>

          {/* Welcome video from Sandra — self-hosted 1:1 clip in the gold TV frame */}
          <div className="mt-9 md:mt-12 mx-auto w-full max-w-[560px]">
            <div
              className="relative rounded-[1.75rem] overflow-hidden border-[3px] border-primary/70 shadow-[0_0_0_1px_rgba(212,175,55,0.25),0_18px_60px_-15px_rgba(212,175,55,0.35)]"
              data-testid="thankyou-video"
            >
              <div className="relative w-full bg-black" style={{ paddingTop: "100%" }}>
                <video
                  src={WELCOME_VIDEO}
                  title="A welcome message from Sandra"
                  className="absolute inset-0 w-full h-full bg-black object-contain"
                  controls
                  playsInline
                  preload="metadata"
                  onPlay={vt.onPlay}
                  onTimeUpdate={vt.onTimeUpdate}
                  onEnded={vt.onEnded}
                  data-testid="thankyou-video-player"
                />
              </div>
            </div>
          </div>

          {/* Email Sandra */}
          <p className="mt-10 md:mt-12 text-base md:text-lg text-white/70 font-medium">
            Have a question before we talk?
          </p>
          <button
            onClick={() => {
              track("contact_open", { location: "thank_you" });
              setOpen(true);
            }}
            data-testid="email-sandra-open"
            className="mt-4 inline-flex items-center justify-center gap-2.5 rounded-2xl bg-primary text-primary-foreground font-black uppercase tracking-wide text-sm md:text-base px-8 py-4 hover:brightness-110 active:scale-[0.98] transition focus-ring shadow-[0_12px_40px_-8px_rgba(212,175,55,0.6)] cursor-pointer"
          >
            <Mail size={18} strokeWidth={2.5} /> Email Sandra
          </button>
          <p className="mt-3 text-[13px] text-white/45">She reads every message personally.</p>
        </div>
      </main>
      <Footer />

      {/* Email Sandra modal */}
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : closeModal())}>
        <DialogContent
          className="max-w-md w-[95vw] p-0 bg-background border-white/10 overflow-hidden"
          data-testid="email-sandra-modal"
        >
          {!sent ? (
            <form onSubmit={submit} className="p-7 md:p-8">
              <DialogTitle className="font-display font-black uppercase tracking-tight text-2xl text-white">
                Email <span className="text-gold-gradient">Sandra</span>
              </DialogTitle>
              <DialogDescription className="mt-1.5 text-sm text-white/60">
                Send Sandra a note and she'll get back to you before your call.
              </DialogDescription>

              <div className="mt-6 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="c-name" className="text-sm font-semibold text-white/80">
                    Your name
                  </Label>
                  <Input
                    id="c-name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="First name"
                    data-testid="email-sandra-name"
                    className="bg-white/[0.04] border-white/15 text-white placeholder:text-white/35"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-email" className="text-sm font-semibold text-white/80">
                    Your email <span className="text-white/45 font-normal">(so she can reply)</span>
                  </Label>
                  <Input
                    id="c-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="you@email.com"
                    data-testid="email-sandra-email"
                    className="bg-white/[0.04] border-white/15 text-white placeholder:text-white/35"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c-message" className="text-sm font-semibold text-white/80">
                    Message
                  </Label>
                  <Textarea
                    id="c-message"
                    value={form.message}
                    onChange={set("message")}
                    placeholder="What's on your mind?"
                    rows={4}
                    required
                    data-testid="email-sandra-message"
                    className="bg-white/[0.04] border-white/15 text-white placeholder:text-white/35 resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={sending || !form.message.trim()}
                data-testid="email-sandra-submit"
                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-primary text-primary-foreground font-black uppercase tracking-wide text-sm px-6 py-3.5 hover:brightness-110 active:scale-[0.98] transition focus-ring shadow-[0_12px_40px_-8px_rgba(212,175,55,0.6)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <>
                    <Loader2 size={17} className="animate-spin" /> Sending…
                  </>
                ) : (
                  <>
                    <Send size={16} strokeWidth={2.5} /> Send Message
                  </>
                )}
              </button>
            </form>
          ) : (
            <div className="p-8 text-center" data-testid="email-sandra-success">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
                <CheckCircle2 size={30} strokeWidth={2} />
              </span>
              <DialogTitle className="mt-5 font-display font-black uppercase tracking-tight text-2xl text-white">
                Message Sent
              </DialogTitle>
              <DialogDescription className="mt-2 text-sm text-white/65 leading-relaxed">
                Thanks{form.name ? `, ${form.name}` : ""} — Sandra has your message and will get
                back to you soon.
              </DialogDescription>
              <button
                onClick={closeModal}
                data-testid="email-sandra-done"
                className="mt-6 inline-flex items-center justify-center rounded-2xl bg-white/[0.06] border border-white/15 text-white font-bold text-sm px-6 py-3 hover:bg-white/10 transition focus-ring"
              >
                Done
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ThankYou;
